import http from "./http"

/**
 * One module per area of the API, each a thin wrapper that returns `data`.
 *
 * Thin on purpose: no caching, no normalisation, no retries. The value is that
 * every URL in the app is written down once, so a route change is one edit rather
 * than a grep — and a screen reading `authApi.login(...)` says what it does
 * without the reader having to decode a path.
 */

export const authApi = {
  /** The credential is the username. An address can be on several accounts, so it
   *  cannot pick one out — see the note on /auth/login in the backend. */
  login: (username, password) => http.post("/auth/login", { username, password }).then((r) => r.data),
  me: () => http.get("/auth/me").then((r) => r.data),
  logout: () => http.post("/auth/logout").then((r) => r.data),
  logoutAll: () => http.post("/auth/logout-all").then((r) => r.data),
  sessions: () => http.get("/auth/sessions").then((r) => r.data),
  revokeSession: (id) => http.delete(`/auth/sessions/${id}`).then((r) => r.data),
  roles: (audience) => http.get("/auth/roles", { params: { audience } }).then((r) => r.data),
  permissionCatalog: (audience) => http.get("/auth/permission-catalog", { params: { audience } }).then((r) => r.data),
  savePreferences: (preferences) => http.patch("/auth/preferences", preferences).then((r) => r.data),
  changePassword: (currentPassword, newPassword) =>
    http.post("/auth/password", { current_password: currentPassword, new_password: newPassword }).then((r) => r.data),

  /** Request a reset link, by username or by email. Always resolves the same way
   *  — see the note on enumeration in the backend's authRoutes. An address on
   *  several accounts produces a link for each. */
  forgotPassword: (username) => http.post("/auth/forgot-password", { username }).then((r) => r.data),

  /** Is this emailed link still good? Lets the screen say so before the person
   *  types a password rather than after. */
  checkResetToken: (token) => http.get(`/auth/reset-password/${encodeURIComponent(token)}`).then((r) => r.data),

  /** Completes an invite and a reset alike — the backend has one endpoint for
   *  both, because they do the same three things. */
  resetPassword: (token, password) => http.post("/auth/reset-password", { token, password }).then((r) => r.data),
  registerDevice: (token, platform = "web") => http.post("/auth/devices", { token, platform }).then((r) => r.data),
  unregisterDevice: (token) => http.delete("/auth/devices", { data: { token } }).then((r) => r.data),
}

export const generalApi = {
  carriers: () => http.get("/general/carriers").then((r) => r.data),
  departments: () => http.get("/general/departments").then((r) => r.data),
  myOrganization: () => http.get("/general/my-organization").then((r) => r.data),
  systemStatus: () => http.get("/general/system-status").then((r) => r.data),
}

export const clientApi = {
  list: () => http.get("/organizations").then((r) => r.data),
  get: (id) => http.get(`/organizations/${id}`).then((r) => r.data),
  nextId: () => http.get("/organizations/next-id").then((r) => r.data),
  create: (payload) => http.post("/organizations", payload).then((r) => r.data),
  update: (id, payload) => http.patch(`/organizations/${id}`, payload).then((r) => r.data),
  /** What clients' industries have been typed as before, for suggestions. */
  industries: () => http.get("/organizations/meta/industries").then((r) => r.data),
  provision: (id) => http.post(`/organizations/${id}/provision`).then((r) => r.data),
  /** `confirm` is required by the server and repeated in the URL on purpose:
   *  this drops the client's database. */
  remove: (id, { force = false } = {}) =>
    http.delete(`/organizations/${id}`, { params: { confirm: id, ...(force ? { force: true } : {}) } }).then((r) => r.data),
}

export const userApi = {
  list: (params) => http.get("/users", { params }).then((r) => r.data),
  directory: (q) => http.get("/users/directory", { params: { q } }).then((r) => r.data),
  get: (id) => http.get(`/users/${id}`).then((r) => r.data),
  create: (payload) => http.post("/users", payload).then((r) => r.data),
  update: (id, payload) => http.patch(`/users/${id}`, payload).then((r) => r.data),
  assignRole: (id, roleId) => http.put(`/users/${id}/role`, { role_id: roleId }).then((r) => r.data),
  resetPassword: (id, password) => http.post(`/users/${id}/password`, { password }).then((r) => r.data),
  /* Multipart, so the Content-Type header the client sets by default has to be
     dropped — axios only writes the boundary when it is choosing the type. */
  uploadAvatar: (file) => {
    const form = new FormData()
    form.append("file", file)
    return http.put("/users/me/avatar", form, { headers: { "Content-Type": undefined } }).then((r) => r.data)
  },
  removeAvatar: () => http.delete("/users/me/avatar").then((r) => r.data),

  /** Send them whatever their account is waiting on — an invite if they never set
   *  a password, a reset link if they did. `type` overrides the choice. */
  resend: (id, type) => http.post(`/users/${id}/resend`, type ? { type } : {}).then((r) => r.data),
  deactivate: (id) => http.delete(`/users/${id}`).then((r) => r.data),
  activate: (id) => http.post(`/users/${id}/activate`).then((r) => r.data),

  /** Is this username free? Asked while typing, so a collision surfaces before
   *  the form is finished rather than as a 409 after it. */
  checkUsername: (username) =>
    http.get("/users/availability", { params: { username } }).then((r) => r.data),
}

/**
 * Time tracking.
 *
 * `running` is polled by the timer bar rather than pushed, because a timer is
 * one row and a socket for it would be a second delivery path to keep in step
 * with the first. The server returns its own clock alongside, so a browser whose
 * clock is minutes out still shows the right elapsed time.
 */
export const timeApi = {
  running: () => http.get("/time/running").then((r) => r.data),
  start: (payload) => http.post("/time/start", payload).then((r) => r.data),
  stop: (payload = {}) => http.post("/time/stop", payload).then((r) => r.data),
  pause: () => http.post("/time/pause").then((r) => r.data),
  resume: () => http.post("/time/resume").then((r) => r.data),

  entries: (params) => http.get("/time/entries", { params }).then((r) => r.data),
  logEntry: (payload) => http.post("/time/entries", payload).then((r) => r.data),
  updateEntry: (id, payload) => http.patch(`/time/entries/${id}`, payload).then((r) => r.data),
  deleteEntry: (id, reason) => http.delete(`/time/entries/${id}`, { data: { reason } }).then((r) => r.data),

  summary: (params) => http.get("/time/summary", { params }).then((r) => r.data),

  pendingSignOff: (params) => http.get("/time/sign-off/pending", { params }).then((r) => r.data),
  signOff: (payload) => http.post("/time/sign-off", payload).then((r) => r.data),
  revokeSignOff: (id, reason) => http.post(`/time/sign-off/${id}/revoke`, { reason }).then((r) => r.data),
  signOffs: (params) => http.get("/time/sign-off", { params }).then((r) => r.data),
}

/**
 * Time reports — totals rather than rows, and its own permission module.
 *
 * `exportCsv` asks axios for a blob and saves it rather than pointing the
 * browser at the URL: the request needs the Authorization header, and a plain
 * link carries no header. An href with a token in the query string would put
 * the credential in history and in every proxy log between here and there.
 */
export const timeReportApi = {
  summary: (params) => http.get("/time-reports", { params }).then((r) => r.data),
  breakdown: (params) => http.get("/time-reports/breakdown", { params }).then((r) => r.data),
  exportCsv: async (params) => {
    const response = await http.get("/time-reports/export", { params, responseType: "blob" })
    const url = URL.createObjectURL(response.data)
    const link = document.createElement("a")
    link.href = url
    /* The server names the file; this is only the fallback for a response that
       arrives without one. */
    link.download = filenameFrom(response.headers?.["content-disposition"]) || "time-report.csv"
    document.body.appendChild(link)
    link.click()
    link.remove()
    // Revoked, or the blob is held for the life of the document.
    URL.revokeObjectURL(url)
  },
}

/** The filename out of a Content-Disposition header, if it carries one. */
function filenameFrom(header) {
  const match = /filename="?([^";]+)"?/.exec(String(header || ""))
  return match ? match[1] : null
}

/** Billing. A client reaches only `status`, `invoices` and `invoice`. */
export const billingApi = {
  status: () => http.get("/billing/status").then((r) => r.data),
  unbilled: (params) => http.get("/billing/unbilled", { params }).then((r) => r.data),
  createInvoice: (payload) => http.post("/billing/invoices", payload).then((r) => r.data),
  invoices: (params) => http.get("/billing/invoices", { params }).then((r) => r.data),
  invoice: (id) => http.get(`/billing/invoices/${id}`).then((r) => r.data),
  discardDraft: (id) => http.delete(`/billing/invoices/${id}`).then((r) => r.data),

  // Recurring charges.
  schedules: (params) => http.get("/billing/schedules", { params }).then((r) => r.data),
  createSchedule: (payload) => http.post("/billing/schedules", payload).then((r) => r.data),
  updateSchedule: (id, payload) => http.patch(`/billing/schedules/${id}`, payload).then((r) => r.data),
  scheduleRuns: (id) => http.get(`/billing/schedules/${id}/runs`).then((r) => r.data),
  runSchedules: () => http.post("/billing/schedules/run").then((r) => r.data),

  // Authorising a card. The form itself is Stripe's page, not ours.
  autopay: (params) => http.get("/billing/autopay", { params }).then((r) => r.data),
  autopaySession: (payload) => http.post("/billing/autopay/session", payload).then((r) => r.data),
  removeAutopay: (payload) => http.delete("/billing/autopay", { data: payload }).then((r) => r.data),
  /** `collection` is asked for, never defaulted: see the route. */
  issue: (id, collection = "send_invoice") =>
    http.post(`/billing/invoices/${id}/issue`, { collection }).then((r) => r.data),
  /** Re-send the email, or re-charge the card — whichever it was issued as. */
  retry: (id) => http.post(`/billing/invoices/${id}/retry`, {}).then((r) => r.data),
  /** Ask Stripe what actually happened to everything still marked open. */
  reconcile: (clientId = null) =>
    http.post("/billing/reconcile", clientId ? { client_id: clientId } : {}).then((r) => r.data),
  /** Which clients can actually be charged, in one request. */
  paymentMethods: () => http.get("/billing/payment-methods").then((r) => r.data),
  void: (id, reason) => http.post(`/billing/invoices/${id}/void`, { reason }).then((r) => r.data),
  settings: (clientId) => http.get(`/billing/settings/${clientId}`).then((r) => r.data),
  saveSettings: (clientId, payload) => http.put(`/billing/settings/${clientId}`, payload).then((r) => r.data),
}

export const roleApi = {
  list: (params) => http.get("/roles", { params }).then((r) => r.data),
  get: (id) => http.get(`/roles/${id}`).then((r) => r.data),
  /** Read from the server rather than from the client's own manifest, so an
   *  admin is always editing what the server actually understands. */
  catalog: (audience) => http.get("/roles/catalog", { params: { audience } }).then((r) => r.data),
  modules: () => http.get("/roles/modules").then((r) => r.data),
  create: (payload) => http.post("/roles", payload).then((r) => r.data),
  update: (id, payload) => http.patch(`/roles/${id}`, payload).then((r) => r.data),
  setPermissions: (id, permissions) => http.put(`/roles/${id}/permissions`, { permissions }).then((r) => r.data),
  deactivate: (id) => http.delete(`/roles/${id}`).then((r) => r.data),
}

export const chatApi = {
  rooms: (params) => http.get("/chat/rooms", { params }).then((r) => r.data),
  supportQueue: () => http.get("/chat/rooms/support-queue").then((r) => r.data),
  room: (id) => http.get(`/chat/rooms/${id}`).then((r) => r.data),
  openDm: (userId) => http.post("/chat/rooms/dm", { user_id: userId }).then((r) => r.data),
  createRoom: (payload) => http.post("/chat/rooms", payload).then((r) => r.data),
  updateRoom: (id, payload) => http.patch(`/chat/rooms/${id}`, payload).then((r) => r.data),
  messages: (id, params) => http.get(`/chat/rooms/${id}/messages`, { params }).then((r) => r.data),
  send: (id, payload) => http.post(`/chat/rooms/${id}/messages`, payload).then((r) => r.data),
  editMessage: (messageId, body) => http.patch(`/chat/messages/${messageId}`, { body }).then((r) => r.data),
  deleteMessage: (messageId) => http.delete(`/chat/messages/${messageId}`).then((r) => r.data),
  markRead: (id, messageId) => http.post(`/chat/rooms/${id}/read`, { message_id: messageId }).then((r) => r.data),
  addParticipants: (id, userIds) => http.post(`/chat/rooms/${id}/participants`, { user_ids: userIds }).then((r) => r.data),
  removeParticipant: (id, userId) => http.delete(`/chat/rooms/${id}/participants/${userId}`).then((r) => r.data),
  close: (id) => http.post(`/chat/rooms/${id}/close`).then((r) => r.data),
  typing: (id, isTyping) => http.post(`/chat/rooms/${id}/typing`, { is_typing: isTyping }).then((r) => r.data),
}

export const projectApi = {
  list: (params) => http.get("/projects", { params }).then((r) => r.data),
  get: (id) => http.get(`/projects/${id}`).then((r) => r.data),
  create: (payload) => http.post("/projects", payload).then((r) => r.data),
  update: (id, payload) => http.patch(`/projects/${id}`, payload).then((r) => r.data),
  addMembers: (id, userIds, role) => http.post(`/projects/${id}/members`, { user_ids: userIds, role }).then((r) => r.data),
  removeMember: (id, userId) => http.delete(`/projects/${id}/members/${userId}`).then((r) => r.data),
  /* `{}` and not `null` as the body: axios serialises null to the four
     characters `null` under a JSON content type, which a strict body parser
     refuses before any route sees it — and the refusal reaches the browser as a
     CORS error, because it carries none of a route response's headers. */
  archive: (id, restore = false) =>
    http.post(`/projects/${id}/archive`, {}, { params: restore ? { restore: true } : {} }).then((r) => r.data),
  /** The same project for another client: its tasks and their steps come too. */
  copy: (id, payload) => http.post(`/projects/${id}/copy`, payload).then((r) => r.data),
}

export const taskApi = {
  /** The whole assignment set. Replace semantics: the first id is the primary. */
  assignees: (id) => http.get(`/tasks/${id}/assignees`).then((r) => r.data),
  setAssignees: (id, userIds) => http.put(`/tasks/${id}/assignees`, { user_ids: userIds }).then((r) => r.data),

  list: (params) => http.get("/tasks", { params }).then((r) => r.data),
  get: (id) => http.get(`/tasks/${id}`).then((r) => r.data),
  statuses: () => http.get("/tasks/statuses").then((r) => r.data),
  create: (payload) => http.post("/tasks", payload).then((r) => r.data),
  update: (id, payload) => http.patch(`/tasks/${id}`, payload).then((r) => r.data),
  comment: (id, body, isInternal = false) =>
    http.post(`/tasks/${id}/comments`, { body, is_internal: isInternal }).then((r) => r.data),
  watch: (id, watching) => http.post(`/tasks/${id}/watch`, { watching }).then((r) => r.data),
  bulk: (payload) => http.post("/tasks/bulk", payload).then((r) => r.data),
  remove: (id) => http.delete(`/tasks/${id}`).then((r) => r.data),
  /** The same shape of work, somewhere else. Its steps come with it. */
  copy: (id, payload) => http.post(`/tasks/${id}/copy`, payload).then((r) => r.data),
  /** One list — a project's tasks, or one task's steps — in the order given. */
  reorder: (ids) => http.post("/tasks/reorder", { ids }).then((r) => r.data),
}

/*
 * Saved views: a named filter combination on a list page.
 *
 * Server-held rather than localStorage, so a view saved at the desk exists on
 * the laptop. `filters` is opaque to the server — the page that wrote it is the
 * only thing that reads it back.
 */
export const savedViewApi = {
  list: (page) => http.get("/views", { params: { page } }).then((r) => r.data),
  save: (page, name, filters) => http.post("/views", { page, name, filters }).then((r) => r.data),
  remove: (id) => http.delete(`/views/${id}`).then((r) => r.data),
}

/**
 * The document store.
 *
 * `upload` posts multipart and has to unset the Content-Type the client sets by
 * default: axios only writes the multipart boundary when it is choosing the
 * header itself, and a boundary-less multipart body is one the server cannot
 * parse. `onProgress` is passed through because a 40MB upload with no feedback
 * is one people cancel and retry.
 */
export const driveApi = {
  list: (params) => http.get("/drive", { params }).then((r) => r.data),
  search: (params) => http.get("/drive/search", { params }).then((r) => r.data),
  tree: (params) => http.get("/drive/tree", { params }).then((r) => r.data),
  trash: (params) => http.get("/drive/trash", { params }).then((r) => r.data),

  createFolder: (payload) => http.post("/drive/folders", payload).then((r) => r.data),
  updateFolder: (id, payload) => http.patch(`/drive/folders/${id}`, payload).then((r) => r.data),
  removeFolder: (id, { purge = false, ...params } = {}) =>
    http.delete(`/drive/folders/${id}`, { params: { ...params, ...(purge ? { purge: true } : {}) } }).then((r) => r.data),
  /* `params` carries `client_id` on every call, not only the ones with a body.
     A back-office account can stand in several tenants' stores, and a call that
     forgets to say which one silently acts on its OWN — same id, different
     schema, no error. */
  restoreFolder: (id, params) => http.post(`/drive/folders/${id}/restore`, {}, { params }).then((r) => r.data),

  upload: (file, { folderId = 0, clientId, onProgress } = {}) => {
    const form = new FormData()
    form.append("file", file)
    form.append("folder_id", String(folderId))
    if (clientId) form.append("client_id", String(clientId))
    return http
      .post("/drive/files", form, {
        headers: { "Content-Type": undefined },
        onUploadProgress: onProgress
          ? (event) => onProgress(event.total ? Math.round((event.loaded / event.total) * 100) : 0)
          : undefined,
      })
      .then((r) => r.data)
  },
  updateFile: (id, payload) => http.patch(`/drive/files/${id}`, payload).then((r) => r.data),
  removeFile: (id, { purge = false, ...params } = {}) =>
    http.delete(`/drive/files/${id}`, { params: { ...params, ...(purge ? { purge: true } : {}) } }).then((r) => r.data),
  restoreFile: (id, params) => http.post(`/drive/files/${id}/restore`, {}, { params }).then((r) => r.data),

  shares: (params) => http.get("/drive/shares", { params }).then((r) => r.data),
  share: (payload) => http.post("/drive/shares", payload).then((r) => r.data),
  unshare: (id, params) => http.delete(`/drive/shares/${id}`, { params }).then((r) => r.data),
}

/**
 * The password vault.
 *
 * `reveal` is a POST with no body, deliberately: a GET would put the entry id
 * in the browser's history and in every access log on the way, and would be
 * prefetchable and replayable from a link. Nothing here caches — the server
 * sends `no-store` and this never keeps a secret beyond the call that asked
 * for it.
 */
export const vaultApi = {
  list: (params) => http.get("/vault", { params }).then((r) => r.data),
  create: (payload) => http.post("/vault", payload).then((r) => r.data),
  update: (id, payload) => http.patch(`/vault/${id}`, payload).then((r) => r.data),
  /* Every one of these takes the store it means, for the same reason the
     document store's do: an id alone is ambiguous across tenants, and the
     ambiguity resolves to the caller's own vault without complaining.

     `{}` and not `null` as the body: axios serialises null to the four
     characters `null` under a JSON content type, and a strict body parser
     rejects that before any route sees it — which arrives in the browser as a
     CORS error, because the refusal carries none of the headers a route's
     response would have. */
  reveal: (id, params) => http.post(`/vault/${id}/reveal`, {}, { params }).then((r) => r.data),
  remove: (id, { purge = false, ...params } = {}) =>
    http.delete(`/vault/${id}`, { params: { ...params, ...(purge ? { purge: true } : {}) } }).then((r) => r.data),
  restore: (id, params) => http.post(`/vault/${id}/restore`, {}, { params }).then((r) => r.data),

  shares: (id, params) => http.get(`/vault/${id}/shares`, { params }).then((r) => r.data),
  share: (id, payload) => http.post(`/vault/${id}/shares`, payload).then((r) => r.data),
  unshare: (id, shareId, params) => http.delete(`/vault/${id}/shares/${shareId}`, { params }).then((r) => r.data),

  audit: (params) => http.get("/vault/audit", { params }).then((r) => r.data),
}

export const notificationApi = {
  list: (params) => http.get("/notifications", { params }).then((r) => r.data),
  unreadCount: () => http.get("/notifications/unread-count").then((r) => r.data),
  markRead: (ids) => http.post("/notifications/read", ids ? { ids } : {}).then((r) => r.data),
  remove: (id) => http.delete(`/notifications/${id}`).then((r) => r.data),
  broadcast: (payload) => http.post("/notifications/broadcast", payload).then((r) => r.data),
}
